"use client";

import {
  PageHeader,
  PageLayout,
  TrustpilotFeedbackModal,
  capitalize,
  useAppContext,
  useFeedbackTrigger,
} from "@repo/dashboard-core";
import { notifications } from "@mantine/notifications";
import { useLocale, useTranslations } from "next-intl";
import { useRouter } from "next/navigation";
import { useCallback, useRef } from "react";

import { FilesTable } from "@/components/my-files/FilesTable";
import { UploadCta } from "@/components/my-files/UploadCta";
import { WelcomeTour } from "@/components/my-files/WelcomeTour";
import {
  getTrustpilotInvitationLink,
  submitFeedback,
} from "@repo/dashboard-core/actions";
import { getFileDownloadUrl } from "@/lib/actions/files";
import { useBrand } from "@/lib/context/BrandContext";
import { triggerDownload } from "@/lib/utils/file-helpers";

type PendingAction = { type: "preview" | "download"; fileName: string };

export default function MyFilesPage() {
  const t = useTranslations("my-files");
  const { userInfo } = useAppContext();
  const router = useRouter();
  const locale = useLocale();
  const brand = useBrand();
  const reloadRef = useRef<() => Promise<void>>(async () => {});
  const pendingActionRef = useRef<PendingAction | null>(null);

  const { applicationId, feedbackOpened, handleTrigger, handleClose } =
    useFeedbackTrigger({ delay: 0 });

  const handleFileAction = useCallback(
    (type: "preview" | "download", fileName: string): boolean => {
      const triggered = handleTrigger();
      if (triggered) {
        pendingActionRef.current = { type, fileName };
      }
      return triggered;
    },
    [handleTrigger],
  );

  const handleFeedbackClose = useCallback(async () => {
    handleClose();
    const action = pendingActionRef.current;
    pendingActionRef.current = null;

    if (!action) return;

    try {
      const urlResult = await getFileDownloadUrl(action.fileName);
      if (!urlResult.success) {
        notifications.show({
          title: t("error"),
          message:
            urlResult.message ||
            (action.type === "preview"
              ? t("preview-failed")
              : t("download-failed")),
          color: "red",
        });
        return;
      }

      if (action.type === "preview") {
        router.push(
          `/${locale}/preview?name=${encodeURIComponent(action.fileName)}`,
        );
      } else {
        const response = await fetch(urlResult.data);
        if (!response.ok) {
          notifications.show({
            title: t("error"),
            message: t("download-failed"),
            color: "red",
          });
          return;
        }
        const blob = await response.blob();
        triggerDownload(blob, action.fileName);
      }
    } catch (err) {
      notifications.show({
        title: t("error"),
        message:
          err instanceof Error
            ? err.message
            : action.type === "preview"
              ? t("preview-failed")
              : t("download-failed"),
        color: "red",
      });
    }
  }, [handleClose, router, locale, t]);

  const registerReload = useCallback((reload: () => Promise<void>) => {
    reloadRef.current = reload;
  }, []);

  return (
    <PageLayout>
      <div className="my-files space-y-8 pb-20">
        {/* Dynamic greeting */}
        <PageHeader
          title={
            userInfo.firstName
              ? `${t("welcome")}, ${capitalize(userInfo.firstName)}!`
              : `${t("welcome")}!`
          }
          description={t("tagline")}
        />

        {/* CTA Banner */}
        <div className="relative overflow-hidden rounded-xl bg-gradient-to-br from-primary to-primary/80 p-6 text-white shadow-shell-lg md:p-8">
          <div className="relative z-10 flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
            <div>
              <h3 className="text-lg font-bold">{t("cta-title")}</h3>
              <p className="mt-1 text-sm text-white/80">
                {t("cta-description")}
              </p>
            </div>
            <UploadCta
              onUploaded={(storedFileName) =>
                router.push(
                  `/${locale}/editor?name=${encodeURIComponent(storedFileName)}`,
                )
              }
              variant="white"
              className="shrink-0"
            />
          </div>
          {/* Decorative circles */}
          <div className="absolute -right-6 -top-6 h-32 w-32 rounded-full bg-white/10" />
          <div className="absolute -bottom-4 -right-4 h-20 w-20 rounded-full bg-white/5" />
        </div>

        {/* Files list */}
        <FilesTable
          registerReload={registerReload}
          onFileAction={handleFileAction}
        />
        <WelcomeTour />
      </div>

      {applicationId && (
        <TrustpilotFeedbackModal
          opened={feedbackOpened}
          onClose={handleFeedbackClose}
          applicationId={applicationId}
          trustpilotWebsiteId={brand.trustpilotWebsiteId}
          domain={brand.domain}
          submitFeedback={submitFeedback}
          getTrustpilotInvitationLink={getTrustpilotInvitationLink}
        />
      )}
    </PageLayout>
  );
}
